import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";

import { useFaqs } from "../../../hooks/useFaqs";
import { useShowPayrollTaxSavings } from "../../../hooks/useShowPayrollTaxSavings";
import { EmployerStatsSummary } from "../../../types/Stats";
import { APButtonLink } from "../../buttons/APButtonLink";
import EmployerActiveLeasesStat from "./EmployerActiveLeasesStat";
import EmployerAnnualisedSavingsStat from "./EmployerAnnualisedSavingsStat";
import EmployerAutoUxRecommendationStat from "./EmployerAutoUxRecommendationStat";
import EmployerAverageAnnualSavingsStat from "./EmployerAverageAnnualSavingsStat";

export const STAT_SHIFT_MS = 500;
const STAT_SHIFT_INTERVAL_MS = 6000;
const homeStatConfig = { useMobileBreakpoints: true };

type EmployerStatsSummariesProps = {
  stats: EmployerStatsSummary;
  config?: {
    desktopLayout?: "single" | "double";
  };
  showAlternate?: boolean;
  showPayrollTaxSavingsCalculation?: boolean;
};

const EmployerStatsSummaries = ({
  stats,
  config,
  showAlternate,
  showPayrollTaxSavingsCalculation,
}: EmployerStatsSummariesProps) => {
  const payrollTaxSavings = useShowPayrollTaxSavings(
    stats.payrollTaxSavings.totalPayrollTaxSavings
  );

  const { faqProps } = useFaqs({
    pageId: "employer-lifetime-payroll-tax-savings",
  });

  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [internalShowAlternate, setInternalShowAlternate] = useState(false);
  const alternateVisible = showAlternate ?? internalShowAlternate;

  useEffect(() => {
    if (showAlternate !== undefined) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setInternalShowAlternate((current) => !current);
    }, STAT_SHIFT_INTERVAL_MS);

    return () => window.clearTimeout(timeoutId);
  }, [showAlternate, internalShowAlternate]);

  if (payrollTaxSavings.isError) {
    throw payrollTaxSavings.error;
  }

  return (
    <>
      <Stack
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: config?.desktopLayout === "single" ? "1fr" : "1fr 1fr",
          },

          rowGap: 1,
          columnGap: {
            xs: 3,
            sm: 6,
            md: 10,
          },
        }}
      >
        <Box>
          <EmployerActiveLeasesStat
            count={stats.activeLeases.count}
            showTitle
            navigateOnClick
            config={homeStatConfig}
          />
        </Box>
        {stats.activeLeases.totalAnnualSavings > 0 && (
          <Box>
            <EmployerAnnualisedSavingsStat
              totalAnnualSavings={stats.activeLeases.totalAnnualSavings}
              totalAnnualPayrolTaxSavings={
                stats.payrollTaxSavings.totalAnnualSavings
              }
              showTitle
              navigateOnClick
              config={homeStatConfig}
              showAlternate={
                alternateVisible && payrollTaxSavings.showPayrollTaxStats
              }
              motion={{
                durationMs: STAT_SHIFT_MS,
                reduceMotion,
              }}
            />
          </Box>
        )}
        {stats.activeLeases.averageAnnualSavings > 0 && (
          <Box>
            <EmployerAverageAnnualSavingsStat
              averageAnnualSavings={stats.activeLeases.averageAnnualSavings}
              lifetimePayrollTaxSavings={
                stats.payrollTaxSavings.totalPayrollTaxSavings
              }
              showTitle
              navigateOnClick
              config={homeStatConfig}
              showAlternate={
                alternateVisible && payrollTaxSavings.showPayrollTaxStats
              }
              motion={{
                durationMs: STAT_SHIFT_MS,
                reduceMotion,
              }}
            />
          </Box>
        )}
        {stats.recommendAutoUxPercentage > 0 && (
          <Box>
            <EmployerAutoUxRecommendationStat
              recommendAutoUxPercentage={stats.recommendAutoUxPercentage}
              showTitle
              navigateOnClick
              config={homeStatConfig}
            />
          </Box>
        )}
      </Stack>
      {payrollTaxSavings.showPayrollTaxStats &&
        showPayrollTaxSavingsCalculation && (
          <Box>
            {faqProps.renderAppLocation(
              "LifetimePayrollTaxSavings-PayrollTaxSavingsCalculation",
              <Box mt={2}>
                <APButtonLink
                  text="^ Payroll Tax Savings Calculation"
                  onClick={() => {}}
                />
              </Box>,
              true
            )}
          </Box>
        )}
    </>
  );
};

export default EmployerStatsSummaries;
